import {
  Component,
  TemplateRef,
  signal,
  computed,
  effect,
  ChangeDetectionStrategy,
  AfterViewInit,
  OnDestroy,
  ViewEncapsulation,
  input,
  output,
  ViewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { injectChatLabels } from "../core/chat-labels";
import { LucideAngularModule, ArrowUp } from "lucide-angular";
import { AgentChatTextarea } from "./ai-chat-textarea";
import { AgentChatAudioRecorder } from "./ai-chat-audio-recorder";
import {
  AgentChatStartTranscribeButton,
  AgentChatCancelTranscribeButton,
  AgentChatFinishTranscribeButton,
  AgentChatAddFileButton,
} from "./ai-chat-buttons";
import { AgentChatToolbar } from "./ai-chat-toolbar";
import { AgentChatToolsMenu } from "./ai-chat-tools-menu";
import type { AiChatInputMode, ToolsMenuItem } from "./ai-chat-input.types";
import { cn } from "../utils/utils";
import { injectChatState } from "../core/chat-state";

export interface SendButtonContext {
  send: () => void;
  disabled: boolean;
  value: string;
}

export interface ToolbarContext {
  mode: AiChatInputMode;
  value: string;
}

/**
 * Agent Chat Input Component
 *
 * Provides a comprehensive chat input interface with optional file and audio features.
 * By default, file upload and audio transcription features are disabled for cleaner UI.
 *
 * @example
 * ```html
 * <!-- Basic chat input (default - only text input and send button) -->
 * <agent-chat-input></agent-chat-input>
 *
 * <!-- Chat input with file upload enabled -->
 * <agent-chat-input [showAddFileButton]="true"></agent-chat-input>
 *
 * <!-- Chat input with audio features enabled -->
 * <agent-chat-input [showAudioFeatures]="true"></agent-chat-input>
 *
 * <!-- Chat input with all features enabled -->
 * <agent-chat-input
 *   [showAddFileButton]="true"
 *   [showAudioFeatures]="true">
 * </agent-chat-input>
 * ```
 */

@Component({
  standalone: true,
  selector: "agent-chat-input",
  host: { "data-agent-ui": "" },
  imports: [
    CommonModule,
    LucideAngularModule,
    AgentChatTextarea,
    AgentChatAudioRecorder,
    AgentChatStartTranscribeButton,
    AgentChatCancelTranscribeButton,
    AgentChatFinishTranscribeButton,
    AgentChatAddFileButton,
    AgentChatToolbar,
    AgentChatToolsMenu,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  template: `
    <div [class]="computedClass()">
      <!-- Left buttons group (file, tools, custom items) -->
      @if (showAddFileButton() || computedToolsMenu().length > 0 || additionalToolbarItems()) {
      <div class="achat-toolbar__group">
        @if (showAddFileButton()) {
        <agent-chat-add-file-button [disabled]="computedMode() === 'transcribe'" (clicked)="handleAddFile()">
        </agent-chat-add-file-button>
        } @if (computedToolsMenu().length > 0) {
        <agent-chat-tools-menu [inputToolsMenu]="computedToolsMenu()" [inputDisabled]="computedMode() === 'transcribe'">
        </agent-chat-tools-menu>
        } @if (additionalToolbarItems()) {
        <ng-container [ngTemplateOutlet]="additionalToolbarItems() || null"></ng-container>
        }
      </div>
      }

      <!-- Main input area: either textarea or audio recorder -->
      @if (computedMode() === "transcribe" && showAudioFeatures()) {
      <agent-chat-audio-recorder [inputShowControls]="true"> </agent-chat-audio-recorder>
      } @else {
      <textarea
        agentChatTextarea
        [inputValue]="computedValue()"
        [inputAutoFocus]="computedAutoFocus()"
        [inputDisabled]="computedMode() === 'processing'"
        [inputClass]="textAreaClass()"
        [inputMaxRows]="textAreaMaxRows()"
        [inputPlaceholder]="textAreaPlaceholder()"
        (keyDown)="handleKeyDown($event)"
        (valueChange)="handleValueChange($event)"
      ></textarea>
      }

      <!-- Right buttons group (send / transcribe controls) -->
      <div class="achat-toolbar__group">
        @if (computedMode() === "transcribe" && showAudioFeatures()) {
        <agent-chat-cancel-transcribe-button (clicked)="handleCancelTranscribe()">
        </agent-chat-cancel-transcribe-button>
        <agent-chat-finish-transcribe-button (clicked)="handleFinishTranscribe()">
        </agent-chat-finish-transcribe-button>
        } @else { @if (showAudioFeatures()) {
        <agent-chat-start-transcribe-button (clicked)="handleStartTranscribe()"> </agent-chat-start-transcribe-button>
        }
        <!-- Send button -->
        <div class="achat-toolbar__send-wrap">
          <button
            type="button"
            [class]="sendButtonClass() || defaultButtonClass"
            [disabled]="!computedValue().trim() || computedMode() === 'processing'"
            (click)="send()"
          >
            <lucide-angular [img]="ArrowUpIcon" [size]="18"></lucide-angular>
          </button>
        </div>
        }
      </div>
    </div>
  `,
})
export class AgentChatInput implements AfterViewInit, OnDestroy {
  @ViewChild(AgentChatTextarea, { read: AgentChatTextarea })
  textAreaRef?: AgentChatTextarea;

  @ViewChild(AgentChatAudioRecorder)
  audioRecorderRef?: AgentChatAudioRecorder;

  // Class inputs for styling default components
  sendButtonClass = input<string | undefined>(undefined);

  toolbarClass = input<string | undefined>(undefined);

  textAreaClass = input<string | undefined>(undefined);

  textAreaMaxRows = input<number | undefined>(undefined);

  textAreaPlaceholder = input<string | undefined>(undefined);

  audioRecorderClass = input<string | undefined>(undefined);

  // Feature flags to control button visibility
  showAddFileButton = input<boolean>(false);

  showAudioFeatures = input<boolean>(false);

  // Regular inputs
  mode = input<AiChatInputMode | undefined>(undefined);

  toolsMenu = input<(ToolsMenuItem | "-")[] | undefined>(undefined);

  autoFocus = input<boolean | undefined>(undefined);

  value = input<string | undefined>(undefined);

  inputClass = input<string | undefined>(undefined);

  additionalToolbarItems = input<TemplateRef<any> | undefined>(undefined);

  // Output events
  submitMessage = output<string>();

  startTranscribe = output<void>();

  cancelTranscribe = output<void>();

  finishTranscribe = output<void>();

  addFile = output<void>();

  valueChange = output<string>();

  readonly ArrowUpIcon = ArrowUp;

  readonly defaultButtonClass = "achat-btn achat-btn--primary";

  readonly labels = injectChatLabels();

  readonly chatState = injectChatState();

  modeSignal = signal<AiChatInputMode>("input");

  toolsMenuSignal = signal<(ToolsMenuItem | "-")[]>([]);

  autoFocusSignal = signal<boolean>(true);

  customClass = signal<string | undefined>(undefined);

  computedMode = computed(() => this.modeSignal());

  computedToolsMenu = computed(() => this.toolsMenu() ?? []);

  computedAutoFocus = computed(() => this.autoFocus() ?? true);

  computedValue = computed(() => {
    const customValue = this.value() ?? "";
    const configValue = this.chatState.inputValue();
    return customValue || configValue || "";
  });

  computedClass = computed(() => cn("achat-input", this.customClass()));

  constructor() {
    effect(() => {
      const currentMode = this.computedMode();
      if (currentMode === "transcribe" && this.audioRecorderRef && this.showAudioFeatures()) {
        this.audioRecorderRef.start().catch(console.error);
      } else if (this.audioRecorderRef?.getState() === "recording" && this.showAudioFeatures()) {
        this.audioRecorderRef.stop().catch(console.error);
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.computedAutoFocus() && this.textAreaRef) {
      setTimeout(() => {
        this.textAreaRef?.focus();
      });
    }
  }

  ngOnDestroy(): void {
    if (this.audioRecorderRef?.getState() === "recording" && this.showAudioFeatures()) {
      this.audioRecorderRef?.stop().catch(console.error);
    }
  }

  handleKeyDown(event: KeyboardEvent): void {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  handleValueChange(value: string): void {
    this.valueChange.emit(value);
    if (this.chatState) this.chatState.changeInput(value);
  }

  send(): void {
    const trimmed = this.computedValue().trim();
    if (trimmed) {
      this.submitMessage.emit(trimmed);
      this.chatState.submitInput(trimmed);
      if (this.chatState) this.chatState.changeInput("");
      if (this.textAreaRef) this.textAreaRef.setValue("");
      if (this.textAreaRef) {
        setTimeout(() => {
          this.textAreaRef?.focus();
        });
      }
    }
  }

  handleStartTranscribe(): void {
    if (this.showAudioFeatures()) {
      this.startTranscribe.emit();
      this.modeSignal.set("transcribe");
    }
  }

  handleCancelTranscribe(): void {
    if (this.showAudioFeatures()) {
      this.cancelTranscribe.emit();
      this.modeSignal.set("input");
    }
  }

  handleFinishTranscribe(): void {
    if (this.showAudioFeatures()) {
      this.finishTranscribe.emit();
      this.modeSignal.set("input");
    }
  }

  handleAddFile(): void {
    if (this.showAddFileButton()) {
      this.addFile.emit();
    }
  }
}
